/**
 * Recover a table's row/column structure from the OCR text already in the graph.
 *
 * A table ingested as a P&ID has no `table_cell` nodes, but OCR did read every value and
 * stored it as an ordinary `tag`. Recovering the structure pre-fills the grid AND gives each
 * cell its real box — a uniform split of the drawn region can't fit a real table, where the
 * header row and first column are far larger than the rest.
 *
 * `pfd_processor._detect_table_structure` does this for paddle detections. Its threshold
 * idea is reused (tolerance from the median text height, not a pixel constant, so nothing is
 * tied to one plant's DPI); its clustering is not, because it emits one row per distinct
 * centre-y (a 3-line cell becomes 3 rows) and clusters columns per-row (a sparse row shifts
 * later columns left). Here rows come from one ordered pass and columns are global.
 *
 * Pure — plain nodes in, plain nodes out.
 */
import type { TableCellNode } from '@/service/graph'
import {
  blankCellAt,
  cellKey,
  type HeaderTypes,
  type TableRegion,
} from './tableGrid'

/** Satisfied by the store's `TagNode`. */
export interface OcrTextNode {
  id: string
  type: string
  x?: number
  y?: number
  width?: number
  height?: number
  content?: string
  label?: string
}

/** A half-open span along one axis. */
export interface Band {
  start: number
  end: number
}

export interface DetectedTable {
  nRows: number
  nCols: number
  /** Row spans, top-down, already expanded to tile the region with no gaps. */
  rowBands: Band[]
  /** Column spans, left-right, likewise tiling. */
  colBands: Band[]
  /** `${row}:${col}` → the text found there (multiple OCR fragments joined). */
  textByKey: Map<string, string>
  /** `${row}:${col}` → how many columns that cell spans (a merged header). 1 unless merged. */
  spanByKey: Map<string, number>
  /** How many of the nRows*nCols coordinates actually carry text. */
  filledCells: number
  /** The nodes read. The caller retires them, so the text isn't counted twice — once in
   *  `tags[]` and again in `tables[]`. */
  sourceIds: string[]
}

/**
 * Tolerance as a multiple of the median text height — the drawing's own type size, so no DPI
 * assumption. Rows: text closer than this vertically is the same row, which folds a 3-line
 * header cell into one. Columns are looser, since inter-column gaps beat word spacing.
 */
const ROW_GAP_FACTOR = 0.6
const COL_GAP_FACTOR = 1.0

const median = (values: number[]): number => {
  if (values.length === 0) return 0
  const s = [...values].sort((a, b) => a - b)
  const mid = s.length >> 1
  return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2
}

type Boxed = OcrTextNode & { x: number; y: number; width: number; height: number; text: string }

const textOf = (n: OcrTextNode): string => (n.content ?? n.label ?? '').trim()

const usable = (n: OcrTextNode): n is Boxed =>
  Number.isFinite(n.x) && Number.isFinite(n.y) &&
  Number.isFinite(n.width) && Number.isFinite(n.height) &&
  (n.width ?? 0) > 0 && (n.height ?? 0) > 0 && textOf(n).length > 0

const centreInside = (n: Boxed, r: TableRegion): boolean => {
  const cx = n.x + n.width / 2
  const cy = n.y + n.height / 2
  return cx >= r.x && cx <= r.x + r.width && cy >= r.y && cy <= r.y + r.height
}

/** Group 1-D spans into bands, splitting only where the gap exceeds `gapThreshold`. One
 *  ordered pass, so a multi-line cell merges into a single band. */
export function clusterBands(spans: Array<[number, number]>, gapThreshold: number): Band[] {
  if (spans.length === 0) return []
  const sorted = [...spans].sort((a, b) => a[0] - b[0])
  const bands: Band[] = []
  let current: Band = { start: sorted[0][0], end: sorted[0][1] }
  for (let i = 1; i < sorted.length; i++) {
    const [s, e] = sorted[i]
    if (s - current.end > gapThreshold) {
      bands.push(current)
      current = { start: s, end: e }
    } else if (e > current.end) {
      current.end = e
    }
  }
  bands.push(current)
  return bands
}

/**
 * Grow bands to tile `[from, to]`: neighbours meet at the midpoint of the whitespace, outer
 * edges reach the region. Otherwise cells are islands around the text — clicks between rows
 * hit nothing and the export carries the text extent, not the cell.
 */
export function tileBands(bands: readonly Band[], from: number, to: number): Band[] {
  if (bands.length === 0) return []
  return bands.map((b, i) => ({
    start: i === 0 ? from : (bands[i - 1].end + b.start) / 2,
    end: i === bands.length - 1 ? to : (b.end + bands[i + 1].start) / 2,
  }))
}

/**
 * The bands a span [from, to] overlaps, as `{ start, count }`.
 *
 * Keyed on the LEFT EDGE, not the centre: a merged header sits centred over its columns, so
 * `74` over a BASE/ALTERNATE pair straddles the boundary between them and its centre falls
 * in the SECOND column — the wrong one. Its left edge is in the first, which is where the
 * cell belongs. `count` is then the merge width, so a cell that covers two columns reports
 * span 2 and the column it swallows carries no cell.
 */
const bandsFor = (bands: readonly Band[], from: number, to: number): { start: number; count: number } => {
  let start = -1
  let count = 0
  for (let i = 0; i < bands.length; i++) {
    // Strict overlap, so text merely touching a boundary doesn't claim the next column.
    if (from < bands[i].end && to > bands[i].start) {
      if (start === -1) start = i
      count++
    }
  }
  if (start === -1) {
    // Entirely inside a seam: fall back to the nearest band.
    let best = 0
    let bestDist = Infinity
    bands.forEach((b, i) => {
      const mid = (from + to) / 2
      const d = mid < b.start ? b.start - mid : mid - b.end
      if (d < bestDist) { bestDist = d; best = i }
    })
    return { start: best, count: 1 }
  }
  return { start, count }
}

export interface DetectOptions {
  /** Node types to read as table text. Defaults to OCR tag nodes. */
  types?: readonly string[]
  rowGapFactor?: number
  colGapFactor?: number
}

/** Returns null when the region holds nothing table-shaped — no text, or a single column,
 *  which is a caption rather than a table. */
export function detectTableFromText(
  nodes: Iterable<OcrTextNode>,
  region: TableRegion,
  opts: DetectOptions = {},
): DetectedTable | null {
  const types = opts.types ?? ['tag']
  const inside: Boxed[] = []
  for (const n of nodes) {
    if (!types.includes(n.type)) continue
    if (!usable(n)) continue
    if (!centreInside(n, region)) continue
    inside.push(n)
  }
  if (inside.length === 0) return null

  // The drawing's type size sets the scale for both axes.
  const scale = median(inside.map((n) => n.height))
  if (scale <= 0) return null

  const rowBands = tileBands(
    clusterBands(inside.map((n) => [n.y, n.y + n.height] as [number, number]),
      scale * (opts.rowGapFactor ?? ROW_GAP_FACTOR)),
    region.y, region.y + region.height,
  )
  // Columns come from the rows that RESOLVE THE MOST of them. A header row's fragments are
  // wide (one per merged group) and bridge the gaps between columns, so including it collapses
  // a BASE/ALTERNATE pair into one: the 19-column steam table read as 10, the 5-column one
  // as 3. Body rows have one fragment per column, so they resolve the full set.
  //
  // Selected by band count, NOT fragment count: a header wrapped over three lines can carry
  // MORE fragments than the body has columns (they stack in the same x range), which would
  // pick exactly the row we need to exclude.
  const colGap = scale * (opts.colGapFactor ?? COL_GAP_FACTOR)
  const rowMembers = rowBands.map((b) =>
    inside.filter((n) => { const cy = n.y + n.height / 2; return cy >= b.start && cy <= b.end }))
  const bandsPerRow = rowMembers.map((r) =>
    clusterBands(r.map((n) => [n.x, n.x + n.width] as [number, number]), colGap).length)
  const mostBands = Math.max(...bandsPerRow)
  // Pool every row that resolves that many, so one row with a missing value can't define the grid.
  const fullRowNodes = rowMembers.filter((_, i) => bandsPerRow[i] === mostBands).flat()
  const colBands = tileBands(
    clusterBands(fullRowNodes.map((n) => [n.x, n.x + n.width] as [number, number]), colGap),
    region.x, region.x + region.width,
  )
  if (rowBands.length === 0 || colBands.length < 2) return null

  // Join fragments sharing a cell in reading order, so a wrapped header becomes one value
  // (same as the Python's `_merge_boxes`).
  const fragments = new Map<string, Boxed[]>()
  const spanByKey = new Map<string, number>()
  for (const n of inside) {
    const r = bandsFor(rowBands, n.y, n.y + n.height).start
    const { start: c, count } = bandsFor(colBands, n.x, n.x + n.width)
    const key = cellKey(r, c)
    const list = fragments.get(key)
    if (list) list.push(n)
    else fragments.set(key, [n])
    // Fragments sharing a cell (a wrapped header) may disagree; the widest wins.
    spanByKey.set(key, Math.max(spanByKey.get(key) ?? 1, count))
  }
  const textByKey = new Map<string, string>()
  for (const [key, list] of fragments) {
    list.sort((a, b) => (a.y - b.y) || (a.x - b.x))
    textByKey.set(key, list.map(textOf).join(' '))
  }

  return {
    nRows: rowBands.length,
    nCols: colBands.length,
    rowBands,
    colBands,
    textByKey,
    spanByKey,
    filledCells: textByKey.size,
    sourceIds: inside.map((n) => n.id),
  }
}

/**
 * Materialize the detection as cells, boxed by row band × column band so the geometry
 * follows the table's real ruling. Every coordinate gets a cell: where OCR found nothing the
 * cell is empty, not absent — a blank is a real observation and the grid must not have gaps.
 */
export function buildTableFromDetection(
  tableIndex: number,
  detected: DetectedTable,
  headerTypes?: HeaderTypes,
): TableCellNode[] {
  const cells: TableCellNode[] = []
  for (let row = 0; row < detected.nRows; row++) {
    const rb = detected.rowBands[row]
    let col = 0
    while (col < detected.nCols) {
      const key = cellKey(row, col)
      let span = Math.min(detected.spanByKey.get(key) ?? 1, detected.nCols - col)
      // Never swallow a coordinate that has text of its own: two fragments can overlap the
      // same band (a wide header plus a stray mark), and skipping the covered column would
      // drop the second one's text silently. Clamp to just before it instead.
      for (let probe = col + 1; probe < col + span; probe++) {
        if (detected.textByKey.has(cellKey(row, probe))) { span = probe - col; break }
      }
      const cb = detected.colBands[col]
      const cbEnd = detected.colBands[col + span - 1]
      cells.push(blankCellAt(
        tableIndex, row, col,
        { x: cb.start, y: rb.start, width: cbEnd.end - cb.start, height: rb.end - rb.start },
        headerTypes,
        detected.textByKey.get(key) ?? '',
        span,
      ))
      // Skip the columns this cell swallowed — they carry no cell of their own.
      col += span
    }
  }
  return cells
}
